import React from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import {
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  InputGroup,
  InputRightElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Skeleton,
  Text,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";

import { useGetMeQuery, useLoginMutation } from "redux/services/apiService";
import { isUserAuthenticated } from "redux/slices/user";
import { useAppSelector } from "redux/store";

type Props = {};
const User: React.FC<Props> = (): JSX.Element => {
  const { t } = useTranslation();

  const isAuthenticated = useAppSelector(isUserAuthenticated);
  const { isLoading, data: user } = useGetMeQuery();

  const [loginMutation] = useLoginMutation();
  const toast = useToast();

  const {
    handleSubmit,
    register,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const { isOpen, onOpen, onClose } = useDisclosure();
  const [show, setShow] = React.useState(false);

  const handleClick = () => setShow(!show);
  const onSubmit = async (values) => {
    try {
      await loginMutation({
        email: values.email,
        password: values.password,
      }).unwrap();

      toast({
        title: t("login_successfully"),
        isClosable: true,
        status: "success",
        duration: 3000,
      });
      onClose();
    } catch (error) {
      console.log(error);
    }
  };

  if (!isAuthenticated) {
    return (
      <>
        <Button onClick={onOpen} colorScheme="brand">
          {t("button_login")}
        </Button>
        <Modal isOpen={isOpen} onClose={onClose}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>{t("login_title")}</ModalHeader>
            <ModalCloseButton />
            <ModalBody pb={6}>
              <FormControl isInvalid={!!errors.email}>
                <FormLabel htmlFor="email">{t("email")}</FormLabel>
                <Input
                  placeholder="email@jogjaprov.go.id"
                  {...register("email")}
                />
                <FormErrorMessage>
                  {errors.email && errors.email.message}
                </FormErrorMessage>
              </FormControl>

              <FormControl mt={4} isInvalid={!!errors.password}>
                <FormLabel htmlFor="password">{t("password")}</FormLabel>
                <InputGroup size="md">
                  <Input
                    pr="4.5rem"
                    type={show ? "text" : "password"}
                    placeholder="Enter password"
                    {...register("password")}
                  />
                  <InputRightElement width="4.5rem">
                    <Button h="1.75rem" size="sm" onClick={handleClick}>
                      {show ? t("button_hide") : t("button_show")}
                    </Button>
                  </InputRightElement>
                </InputGroup>
              </FormControl>
            </ModalBody>

            <ModalFooter>
              <Button mr={3} onClick={onClose} disabled={isSubmitting}>
                {t("button_cancel")}
              </Button>
              <Button
                colorScheme="brand"
                isLoading={isSubmitting}
                onClick={handleSubmit(onSubmit)}
              >
                {t("button_login")}
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </>
    );
  }

  return (
    <Skeleton isLoaded={!isLoading}>
      <Flex flexDirection="column" alignItems="end">
        <Text as={"span"} fontWeight="semibold" color={"#5B5B5B"}>
          {user?.name}
        </Text>
        <Text as={"span"} fontWeight="semibold">
          {user?.role}
        </Text>
      </Flex>
    </Skeleton>
  );
};

export default User;
